import { FileDropzone, SelectedFilesList } from '@/shared/components';
import { ATTACHMENT_FILE_TYPES } from '@/shared/lib/fileTypes';
import { Button } from '@/shared/ui/button';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { HelpCircle, X, XCircle } from 'lucide-react';
import { useState } from 'react';

interface RejectPackageModalProps {
  open?: boolean; // Support both isOpen and open
  isOpen?: boolean; // Legacy support
  onClose: () => void;
  onConfirm?: (comments: string, file?: File) => void; // Legacy support
  onSubmit?: (comments: string, files?: File[]) => void; // New interface
  packageRef?: string;
  jobTitle?: string;
  targetName?: string; // New: generic target name
  showFileUpload?: boolean;
  mode?: 'reject' | 'request-info';
  isSubmitting?: boolean;
}

export function RejectPackageModal({
  open,
  isOpen,
  onClose,
  onConfirm,
  onSubmit,
  packageRef,
  jobTitle,
  targetName,
  showFileUpload = true, // Default to true now
  mode = 'reject',
  isSubmitting = false,
}: RejectPackageModalProps) {
  const [comments, setComments] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const isModalOpen = open ?? isOpen ?? false;
  const MAX_COMMENTS = 500;
  const isOverLimit = comments.length > MAX_COMMENTS;

  if (!isModalOpen) return null;

  const handleFilesAccepted = (accepted: File[]) => {
    setSelectedFiles((prev) => [...prev, ...accepted]);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    if (comments.trim()) {
      // Support both callback interfaces
      if (onSubmit) {
        onSubmit(comments, selectedFiles.length > 0 ? selectedFiles : undefined);
      } else if (onConfirm) {
        onConfirm(comments, selectedFiles[0]);
        setComments('');
        setSelectedFiles([]);
        onClose();
      }
    }
  };

  const handleCancel = () => {
    setComments('');
    setSelectedFiles([]);
    onClose();
  };

  const isRejectMode = mode === 'reject';

  const title = isRejectMode
    ? targetName
      ? `Reject ${targetName}`
      : jobTitle
        ? 'Reject Job'
        : packageRef
          ? `Reject Package ${packageRef}`
          : 'Reject Package'
    : targetName
      ? `Request Info for ${targetName}`
      : jobTitle
        ? 'Request More Information'
        : packageRef
          ? `Request Info for Package ${packageRef}`
          : 'Request More Information';

  const subtitle = isRejectMode
    ? 'Please provide a reason for rejection'
    : 'Please specify what additional information is required';

  const placeholder = isRejectMode
    ? 'Please provide detailed reasons for rejecting this job...'
    : 'Please describe what additional information or clarification is needed...';

  const buttonLabel = isRejectMode ? 'Confirm Rejection' : 'Send Request';
  const buttonColor = isRejectMode
    ? 'bg-red-600 hover:bg-red-700'
    : 'bg-amber-600 hover:bg-amber-700';
  const iconBg = isRejectMode ? 'bg-red-50 dark:bg-red-950' : 'bg-amber-50 dark:bg-amber-950';
  const iconColor = isRejectMode ? 'text-red-600' : 'text-amber-600';
  const Icon = isRejectMode ? XCircle : HelpCircle;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background border border-border rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
              <Icon className={`w-5 h-5 ${iconColor}`} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{title}</h2>
              <p className="text-sm text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={handleCancel} className="shrink-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Comments Field */}
          <div className="space-y-2">
            <Label htmlFor="rejection-comments" className="text-sm font-medium">
              {isRejectMode ? 'Rejection Comments' : 'Information Request'}{' '}
              <span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="rejection-comments"
              placeholder={placeholder}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={6}
              className="resize-none"
            />
            <p
              className={`text-xs ${isOverLimit ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}
            >
              {comments.length} / {MAX_COMMENTS} characters
              {isOverLimit && ' — limit exceeded'}
            </p>
          </div>

          {/* File Upload (only shown if enabled) */}
          {showFileUpload && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Supporting Documentation (Optional)</Label>
              <FileDropzone
                preset={ATTACHMENT_FILE_TYPES}
                multiple
                disabled={isSubmitting}
                onFilesAccepted={handleFilesAccepted}
              />
              <SelectedFilesList
                files={selectedFiles}
                onRemove={handleRemoveFile}
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={handleCancel} className="border-border">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!comments.trim() || isOverLimit || isSubmitting}
            className={buttonColor + ' text-white'}
          >
            <XCircle className="w-4 h-4 mr-2" />
            {isSubmitting ? 'Submitting...' : buttonLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
